import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, errMsg, todayISO } from "../lib/api";

const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-300 bg-white focus:border-slate-900 outline-none transition-colors";

export const tzTime = (iso, tz) =>
  iso ? new Date(iso).toLocaleTimeString("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }) : "";

const Field = ({ label, children }) => (
  <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">{label}</span>{children}</label>
);

export const CorrectionDialog = ({ open, onOpenChange, record, workers, tz, onSaved }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(record
      ? { check_in_time: tzTime(record.check_in, tz), check_out_time: tzTime(record.check_out, tz), note: record.note || "" }
      : { worker_id: "", date: todayISO(), check_in_time: "", check_out_time: "", note: "" });
  }, [open, record, tz]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!record && !f.worker_id) return toast.error("Select a worker");
    setBusy(true);
    const body = { check_in_time: f.check_in_time, check_out_time: f.check_out_time || null, note: f.note };
    try {
      if (record) await api.put(`/attendance/${record.id}`, body);
      else await api.post("/attendance", { ...body, worker_id: f.worker_id, date: f.date });
      toast.success(record ? "Record corrected" : "Manual entry added");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="correction-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{record ? "Correct attendance" : "Add manual entry"}</DialogTitle>
          {record && <p className="text-sm text-slate-500">{record.worker_name} · <span className="font-mono">{record.worker_code}</span> · {record.date}</p>}
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          {!record && (
            <>
              <div className="col-span-2">
                <Field label="Worker">
                  <Select value={f.worker_id} onValueChange={(v) => setF({ ...f, worker_id: v })}>
                    <SelectTrigger data-testid="correction-worker-select"><SelectValue placeholder="Select worker" /></SelectTrigger>
                    <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Date"><input type="date" required max={todayISO()} value={f.date || ""} onChange={set("date")} data-testid="correction-date-input" className={inputCls} /></Field>
              </div>
            </>
          )}
          <Field label="Check-in time"><input type="time" required value={f.check_in_time || ""} onChange={set("check_in_time")} data-testid="correction-check-in-input" className={inputCls} /></Field>
          <Field label="Check-out time (optional)"><input type="time" value={f.check_out_time || ""} onChange={set("check_out_time")} data-testid="correction-check-out-input" className={inputCls} /></Field>
          <div className="col-span-2">
            <Field label="Reason / note"><input value={f.note || ""} onChange={set("note")} placeholder="e.g. Forgot to punch out" data-testid="correction-note-input" className={inputCls} /></Field>
          </div>
          <p className="col-span-2 text-xs text-slate-500">Times are in {tz}. Status, late minutes and hours are recalculated automatically.</p>
          <button disabled={busy} data-testid="correction-save-btn" className="col-span-2 h-11 rounded-full bg-slate-900 hover:bg-emerald-500 hover:text-slate-950 text-white font-semibold disabled:opacity-50 transition-colors">
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
